(ns io.daskul.webapp.widgets.embedded
  "Embedded relationship UI components (F-016).

   Provides components for displaying and editing embedded has-many
   and has-one relationships within aggregate forms."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.format :as format]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- format-field-label
  "Convert field name to human-readable label.
   :suggested-frequency or \"suggested-frequency\" -> \"Suggested Frequency\""
  [field-name]
  (->> (-> (name field-name)
           (str/split #"-"))
       (map str/capitalize)
       (str/join " ")))

(defn- field-name->keyword
  "Ensure field name is a keyword (may come as string from JSON)."
  [field-name]
  (if (string? field-name) (keyword field-name) field-name))

(defn- format-value
  "Format a value for display in the table."
  [value field-name]
  (cond
    (nil? value) "-"
    (= field-name :price) (format/format-currency value)
    :else (str value)))

;; ============================================================================
;; Embedded Form Modal
;; ============================================================================

(defui embedded-form-modal
  "Modal form for adding/editing embedded items.

   Props:
   - opened?: boolean - whether modal is open
   - on-close: fn - called when modal should close
   - agg-key: keyword - parent aggregate key
   - parent-id: string - parent record ID
   - rel-name: keyword - relationship name
   - rel-config: map - relationship config with :fields
   - mode: :add or :edit
   - initial-data: map - initial form data (for edit mode)
   - item-key: string - item key (for edit mode)"
  [{:keys [opened? on-close agg-key parent-id rel-name rel-config mode initial-data item-key]}]
  (let [fields (:fields rel-config)
        saving? (urf/use-subscribe [::agg.subs/embedded-saving? agg-key parent-id rel-name])
        form-error (urf/use-subscribe [::agg.subs/embedded-form-error agg-key parent-id rel-name])
        [form-data set-form-data!] (uix/use-state (or initial-data {}))
        label (format-field-label rel-name)
        title (if (= mode :edit) (str "Edit " label) (str "Add " label))

        ;; Get valid field keys from config
        valid-field-keys (set (map #(field-name->keyword (:name %)) fields))

        handle-submit
        (fn [e]
          (.preventDefault e)
          (.stopPropagation e)  ; Prevent bubbling to parent form
          ;; Only send fields that are in the schema (filter out any stray keys)
          (let [clean-data (select-keys form-data valid-field-keys)]
            (js/console.log "Submitting embedded form:" (clj->js clean-data))
            (if (= mode :edit)
              (rf/dispatch [::agg.events/patch-embedded agg-key parent-id rel-name item-key clean-data])
              (rf/dispatch [::agg.events/add-embedded agg-key parent-id rel-name clean-data]))))

        handle-close
        (fn []
          (set-form-data! {})
          (on-close))]

    ;; Reset form when modal opens with new initial data
    (uix/use-effect
     (fn []
       (when opened?
         (set-form-data! (or initial-data {})))
       js/undefined)
     [opened? initial-data])

    ($ ui/modal
      {:opened opened?
       :onClose handle-close
       :title title
       :centered true}
      ($ :form {:onSubmit handle-submit}
        ($ ui/stack {:gap "md"}
          ;; Form-level error
          (when (:message form-error)
            ($ ui/error-alert {:mb "sm"}
              (:message form-error)))

          ;; Form fields
          (for [field fields]
            (let [field-name (:name field)
                  field-kw (field-name->keyword field-name)
                  required? (:required field)
                  field-error (get-in form-error [:field-errors field-kw])]
              ($ ui/text-input
                {:key (str field-name)
                 :label (format-field-label field-kw)
                 :value (or (get form-data field-kw) "")
                 :onChange #(set-form-data! (assoc form-data field-kw (.. % -target -value)))
                 :required required?
                 :error field-error})))

          ;; Buttons
          ($ ui/group {:justify "flex-end" :mt "md"}
            ($ ui/button
              {:variant "subtle" :color "gray"
               :onClick handle-close}
              "Cancel")
            ($ ui/button
              {:type "submit"
               :loading saving?}
              (if (= mode :edit) "Save" "Add"))))))))

;; ============================================================================
;; Has-Many List Component
;; ============================================================================

(defui embedded-list
  "Table component for has-many embedded relationships.

   Props:
   - agg-key: Parent aggregate key (e.g., :program)
   - parent-id: Parent record ID
   - rel-name: Relationship name (e.g., :packages)
   - rel-config: Relationship config from /_config

   Items from API are structured as: {:key uuid, :data {...item-fields...}}"
  [{:keys [agg-key parent-id rel-name rel-config]}]
  (let [items (urf/use-subscribe [::agg.subs/embedded-items agg-key parent-id rel-name])
        loading? (urf/use-subscribe [::agg.subs/embedded-loading? agg-key parent-id rel-name])
        save-success? (urf/use-subscribe [::agg.subs/embedded-save-success? agg-key parent-id rel-name])
        error (urf/use-subscribe [::agg.subs/embedded-error agg-key parent-id rel-name])
        fields (:fields rel-config)
        label (format-field-label rel-name)
        ;; Modal state
        [modal-open? set-modal-open!] (uix/use-state false)
        [modal-mode set-modal-mode!] (uix/use-state :add)
        [edit-data set-edit-data!] (uix/use-state nil)
        [edit-key set-edit-key!] (uix/use-state nil)

        handle-add
        (fn []
          (rf/dispatch [::agg.events/clear-embedded-save-success agg-key parent-id rel-name])
          (set-modal-mode! :add)
          (set-edit-data! nil)
          (set-edit-key! nil)
          (set-modal-open! true))

        handle-edit
        (fn [item-key item-data]
          (rf/dispatch [::agg.events/clear-embedded-save-success agg-key parent-id rel-name])
          (set-modal-mode! :edit)
          (set-edit-data! item-data)
          (set-edit-key! item-key)
          (set-modal-open! true))

        handle-modal-close
        (fn []
          (set-modal-open! false)
          (set-edit-data! nil)
          (set-edit-key! nil))]

    ;; Close modal on successful save
    (uix/use-effect
     (fn []
       (when save-success?
         (set-modal-open! false)
         (set-edit-data! nil)
         (set-edit-key! nil)
         (rf/dispatch [::agg.events/clear-embedded-save-success agg-key parent-id rel-name]))
       js/undefined)
     [save-success? agg-key parent-id rel-name])

    ;; Fetch embedded items on mount
    (uix/use-effect
     (fn []
       (when parent-id
         (rf/dispatch [::agg.events/list-embedded agg-key parent-id rel-name]))
       js/undefined)
     [agg-key parent-id rel-name])

    ($ :<>
      ;; Add/Edit modal
      ($ embedded-form-modal
        {:opened? modal-open?
         :on-close handle-modal-close
         :agg-key agg-key
         :parent-id parent-id
         :rel-name rel-name
         :rel-config rel-config
         :mode modal-mode
         :initial-data edit-data
         :item-key edit-key})

      ($ ui/box {:mt "xl"}
        ($ ui/group {:justify "space-between" :mb "sm"}
          ($ ui/title {:order 4} label)
          ($ ui/button
            {:size "xs"
             :variant "light"
             :leftSection ($ icons/icon-plus {:size 16})
             :onClick handle-add}
            "Add"))

        (cond
          loading?
          ($ ui/centered-loader {:py "md" :size "sm"})

          error
          ($ ui/error-alert "Failed to load items")

          (empty? items)
          ($ ui/text {:c "dimmed" :size "sm"}
            (str "No " (str/lower-case label) " defined yet."))

          :else
          ($ ui/table {:striped true :withTableBorder true :withColumnBorders true}
            ($ ui/thead
              ($ ui/tr
                (for [field fields]
                  (let [field-kw (field-name->keyword (:name field))]
                    ($ ui/th {:key (str (:name field))}
                      (format-field-label field-kw))))
                ($ ui/th {:key "actions"} "")))
            ($ ui/tbody
              (for [{:keys [key data]} items]
                ($ ui/tr {:key (str key)}
                  (for [field fields]
                    (let [field-kw (field-name->keyword (:name field))
                          value (get data field-kw)]
                      ($ ui/td {:key (str (:name field))}
                        (format-value value field-kw))))
                  ($ ui/td {:key "actions"}
                    ($ ui/group {:gap "xs"}
                      ($ ui/action-icon
                        {:variant "subtle"
                         :size "sm"
                         :onClick #(handle-edit key data)}
                        ($ icons/icon-edit {:size 14}))
                      ($ ui/action-icon
                        {:variant "subtle"
                         :color "red"
                         :size "sm"
                         :disabled true}  ; Disabled until delete support added
                        ($ icons/icon-trash {:size 14})))))))))))))
